import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Grid3x3, Undo2, RotateCcw, Layers, Bath, Ruler } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { defaultState, applyActions, walls } from "./cadGeometry";
import Room3D from "./Room3D";
import PlanView from "./PlanView";
import CommandBar from "./CommandBar";
import Moodboard from "./Moodboard";
import { RoomPanel, SurfacePanel, FixturePanel } from "./StudioPanels";

const TABS = [["surfaces", "Surfaces", Layers], ["fixtures", "Fixtures", Bath], ["room", "Room", Ruler]];
const tabCls = (on) => `flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs transition-colors ${on ? "bg-rossi-gold text-rossi-bg" : "text-rossi-cream/70 hover:text-rossi-cream"}`;

export default function CadStudio() {
  const [collections, setCollections] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [brassware, setBrassware] = useState([]);
  const [state, setState] = useState(defaultState);
  const [past, setPast] = useState([]);
  const [view, setView] = useState("3d");
  const [panel, setPanel] = useState("surfaces");
  const [selSurface, setSelSurface] = useState("w0");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [params] = useSearchParams();
  const glRef = useRef(null);
  const viewportRef = useRef(null);
  const tileApplied = useRef(false);
  const meta = useMemo(() => Object.fromEntries(fixtures.map((f) => [f.id, f])), [fixtures]);

  useEffect(() => {
    Promise.all([api.get("/collections"), api.get("/cad/fixtures")]).then(([c, f]) => {
      const all = [...(c.data || []), ...(f.data?.panels || [])];
      setCollections(all);
      setFixtures(f.data?.items || []);
      setBrassware(f.data?.brassware || []);
      const t = (c.data || []).find((x) => x.id === params.get("tile"));
      if (t && !tileApplied.current) {
        tileApplied.current = true;
        const target = t.family === "Legno" ? "floor" : "w0";
        setState((s) => applyActions(s, [{ op: "set_surface", target, tile_id: t.id, tile_w: t.tile_w, tile_h: t.tile_h }], {}, all));
        setSelSurface(target);
        toast.success(`${t.name} applied — explore it in 3D`);
      }
    }).catch(() => toast.error("Could not load The Studio catalogue."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = (actions) => { setPast((p) => [...p.slice(-40), state]); setState(applyActions(state, actions, meta, collections)); };
  const undo = () => { if (!past.length) return; setState(past[past.length - 1]); setPast((p) => p.slice(0, -1)); };
  const reset = () => { setPast((p) => [...p, state]); setState(defaultState()); setSelected(null); };
  const liveMove = (uid, x, z) => setState((s) => applyActions(s, [{ op: "move_fixture", uid, x, z }], meta, collections));
  const dragStart = () => setPast((p) => [...p.slice(-40), state]);
  const pickSurface = (k) => { setSelSurface(k); setPanel("surfaces"); };
  const pickFixture = (uid) => { setSelected(uid); if (uid) setPanel("fixtures"); };

  const runCommand = async (q) => {
    setBusy(true);
    try {
      const aiState = { ...state, walls: walls(state.room).map((w) => ({ key: w.key, label: w.label, length_m: +w.len.toFixed(2) })) };
      const { data } = await api.post("/cad/command", { command: q, state: aiState });
      if (data.actions?.length) commit(data.actions);
      setLog((l) => [...l, { q, reply: data.reply }]);
    } catch (e) { toast.error(e?.response?.data?.detail || "The Studio couldn't apply that. Try rephrasing."); }
    finally { setBusy(false); }
  };

  const getSnapshot = async () => {
    if (view !== "3d") { setView("3d"); await new Promise((r) => setTimeout(r, 900)); }
    try { return glRef.current?.domElement.toDataURL("image/png"); } catch (e) { return null; }
  };

  const shared = { state, meta, brassware, selected, onSelect: pickFixture, onMove: liveMove, onDragStart: dragStart, selSurface, onSurface: pickSurface };

  return (
    <div data-testid="cad-studio">
      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        <div className="min-w-0">
          <CommandBar onRun={runCommand} busy={busy} log={log} />
          <div ref={viewportRef} className="relative h-[440px] sm:h-[560px] rounded-2xl overflow-hidden border border-rossi-gold/20 scroll-mt-24 cad-cursor" data-testid="cad-viewport">
            {view === "3d" ? <Room3D {...shared} collections={collections} glRef={glRef} /> : <PlanView {...shared} />}
            <div className="absolute top-3 left-3 flex gap-1.5">
              <div className="flex rounded-full bg-rossi-card/90 backdrop-blur border border-rossi-cream/15 p-1">
                <button onClick={() => setView("3d")} className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1 ${view === "3d" ? "bg-rossi-gold text-rossi-bg" : "text-rossi-cream"}`} data-testid="view-3d"><Box size={13} /> 3D</button>
                <button onClick={() => setView("plan")} className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1 ${view === "plan" ? "bg-rossi-gold text-rossi-bg" : "text-rossi-cream"}`} data-testid="view-plan"><Grid3x3 size={13} /> Plan</button>
              </div>
              <button onClick={undo} disabled={!past.length} className="h-9 w-9 rounded-full bg-rossi-card/90 backdrop-blur border border-rossi-cream/15 text-rossi-cream flex items-center justify-center disabled:opacity-40" title="Undo" data-testid="cad-undo"><Undo2 size={15} /></button>
              <button onClick={reset} className="h-9 w-9 rounded-full bg-rossi-card/90 backdrop-blur border border-rossi-cream/15 text-rossi-cream flex items-center justify-center" title="Reset room" data-testid="cad-reset"><RotateCcw size={15} /></button>
            </div>
            <div className="absolute top-3 right-3 rounded-full bg-rossi-card/90 backdrop-blur border border-rossi-cream/15 px-3 py-1.5 text-[11px] font-mono text-rossi-gold" data-testid="cad-dims">{Math.round(state.room.length * 1000)} × {Math.round(state.room.width * 1000)} × {Math.round(state.room.height * 1000)} mm</div>
            <p className="absolute bottom-3 left-3 right-3 text-[11px] text-rossi-cream/70 pointer-events-none">{view === "3d" ? "Drag to orbit · scroll to zoom · drag a fixture to move it · click a wall or floor to tile it" : "Drag fixtures to position (50 mm snap) · click a wall to tile it"}</p>
          </div>
        </div>
        <aside className="card-lux p-4 h-fit lg:sticky lg:top-24" data-testid="cad-sidebar">
          <div className="flex gap-1 bg-rossi-bg rounded-xl p-1 mb-4">
            {TABS.map(([k, l, Icon]) => <button key={k} onClick={() => setPanel(k)} className={tabCls(panel === k)} data-testid={`cad-tab-${k}`}><Icon size={13} /> {l}</button>)}
          </div>
          {panel === "surfaces" && <SurfacePanel state={state} collections={collections} sel={selSurface} setSel={setSelSurface} update={(a) => commit([a])} />}
          {panel === "fixtures" && <FixturePanel state={state} fixtures={fixtures} meta={meta} brassware={brassware} setBrass={(id) => commit([{ op: "set_brassware", item: id }])} selected={selected}
            add={(type) => {
              const m = meta[type];
              commit([{ op: "add_fixture", type, x: state.room.length / 2, z: ["niche", "mirror", "mirror_rect", "shower_head", "towel_rail"].includes(m?.model) ? m.d / 2 : state.room.width / 2 }]);
              toast.success("Added — drag it into place");
              if (window.innerWidth < 1024) viewportRef.current?.scrollIntoView({ behavior: "smooth" });
            }}
            patch={(uid, p) => commit([{ op: "move_fixture", uid, ...p }])} remove={(uid) => { commit([{ op: "remove_fixture", uid }]); setSelected(null); }} />}
          {panel === "room" && <RoomPanel state={state} update={(a) => commit([a])} />}
        </aside>
      </div>
      <Moodboard state={state} collections={collections} meta={meta} brassware={brassware} getSnapshot={getSnapshot} />
    </div>
  );
}
